import {
  CognitoUserPool,
  CognitoUser,
  CognitoUserAttribute,
  AuthenticationDetails,
} from "amazon-cognito-identity-js";
import { COGNITO } from "../config/cognitoConfig";

// Sign-up, email confirmation and sign-in (SRP) against the GRC Cognito user pool.
// Anyone can sign up, but a new account has no role until an admin approves it (see DEPLOY.md).
// Users an admin creates directly answer a "set a new password" challenge on first sign-in,
// which is handled by completeNewPassword().

let pool = null;
let pendingUser = null;

function getPool() {
  if (!pool) {
    pool = new CognitoUserPool({
      UserPoolId: COGNITO.userPoolId,
      ClientId: COGNITO.clientId,
    });
  }
  return pool;
}

function toUser(session) {
  const claims = session.getIdToken().payload;
  return {
    email: claims.email,
    name: claims.name || claims.email,
    groups: claims["cognito:groups"] || [],
    clientId: claims["custom:client_id"] || null,
  };
}

/** Resolves with the signed-in user, or null when there is no valid session. */
export function restoreSession() {
  return new Promise((resolve) => {
    const current = getPool().getCurrentUser();
    if (!current) return resolve(null);
    current.getSession((err, session) => {
      resolve(err || !session || !session.isValid() ? null : toUser(session));
    });
  });
}

/** Resolves {user} when signed in, or {newPasswordRequired: true}. */
export function signIn(email, password) {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: getPool() });
    const details = new AuthenticationDetails({ Username: email, Password: password });
    user.authenticateUser(details, {
      onSuccess: (session) => resolve({ user: toUser(session) }),
      onFailure: reject,
      newPasswordRequired: () => {
        pendingUser = user;
        resolve({ newPasswordRequired: true });
      },
    });
  });
}

export function completeNewPassword(newPassword) {
  return new Promise((resolve, reject) => {
    if (!pendingUser) return reject(new Error("Sign in again to set a new password."));
    pendingUser.completeNewPasswordChallenge(
      newPassword,
      {},
      {
        onSuccess: (session) => {
          pendingUser = null;
          resolve({ user: toUser(session) });
        },
        onFailure: reject,
      }
    );
  });
}

/** Creates the account. Cognito emails a confirmation code. Resolves true when a code was sent. */
export function signUp(name, email, password) {
  return new Promise((resolve, reject) => {
    const attributes = [
      new CognitoUserAttribute({ Name: "name", Value: name }),
      new CognitoUserAttribute({ Name: "email", Value: email }),
    ];
    getPool().signUp(email, password, attributes, null, (err) => (err ? reject(err) : resolve(true)));
  });
}

/** Confirms the email address with the code that was emailed. */
export function confirmSignUp(email, code) {
  return new Promise((resolve, reject) => {
    new CognitoUser({ Username: email, Pool: getPool() }).confirmRegistration(code.trim(), true, (err) =>
      err ? reject(err) : resolve(true)
    );
  });
}

export function resendCode(email) {
  return new Promise((resolve, reject) => {
    new CognitoUser({ Username: email, Pool: getPool() }).resendConfirmationCode((err) =>
      err ? reject(err) : resolve(true)
    );
  });
}

/**
 * Asks Cognito for fresh tokens so a role or department an admin just granted shows up
 * without signing out. Resolves the refreshed user, or null if the session has ended.
 */
export function refreshUser() {
  return new Promise((resolve) => {
    const current = getPool().getCurrentUser();
    if (!current) return resolve(null);
    current.getSession((err, session) => {
      if (err || !session) return resolve(null);
      current.refreshSession(session.getRefreshToken(), (refreshError, fresh) => {
        resolve(refreshError || !fresh ? null : toUser(fresh));
      });
    });
  });
}

export function signOut() {
  const current = getPool().getCurrentUser();
  if (current) current.signOut();
}

/**
 * The ID token is sent to the API (not the access token) because only the ID
 * token carries the user's client assignment ("custom:client_id").
 * getSession refreshes an expired token automatically.
 */
export function getIdToken() {
  return new Promise((resolve) => {
    const current = getPool().getCurrentUser();
    if (!current) return resolve(null);
    current.getSession((err, session) => {
      resolve(err || !session ? null : session.getIdToken().getJwtToken());
    });
  });
}
